import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Divider,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  Phone,
  Email,
  Assignment,
  Timeline,
  LocationOn,
  Block,
  Warning
} from '@mui/icons-material';
import MaidStatusToggle from './MaidStatusToggle';

const MaidQuickActions = ({ 
  maidProfile, 
  onProfileUpdate,
  onStatusChange,
  onViewDetails,
  onRejectMaid,
  variant = 'menu', // 'menu' | 'inline'
  showStatusToggle = true 
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewDetails = () => {
    onViewDetails?.(maidProfile);
    handleMenuClose();
  };


  const handleContactPhone = () => {
    if (maidProfile?.user?.phoneNumber) {
      window.open(`tel:${maidProfile.user.phoneNumber}`);
    }
    handleMenuClose();
  };

  const handleContactEmail = () => {
    if (maidProfile?.user?.email) {
      window.open(`mailto:${maidProfile.user.email}`);
    }
    handleMenuClose();
  };

  const handleViewBookings = () => {
    // Navigate to bookings page with maid filter
    console.log('View bookings for maid:', maidProfile.id);
    handleMenuClose();
  };

  const handleViewPerformance = () => {
    // Navigate to performance analytics
    console.log('View performance for maid:', maidProfile.id);
    handleMenuClose();
  };

  const handleViewServiceAreas = () => {
    // Navigate to map view
    console.log('View service areas for maid:', maidProfile.id);
    handleMenuClose();
  };

  const handleRejectClick = () => {
    setRejectDialogOpen(true);
    handleMenuClose();
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      return; // Don't proceed without reason
    }
    
    setRejecting(true);
    try {
      await onRejectMaid?.(maidProfile, rejectReason);
      setRejectDialogOpen(false);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting maid:', error);
    } finally {
      setRejecting(false);
    }
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setRejectReason('');
  };


  if (variant === 'inline') {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Tooltip title="Manage Profile">
          <IconButton size="small" onClick={() => onViewDetails?.(maidProfile)}>
            <Visibility />
          </IconButton>
        </Tooltip>
        
        
        {maidProfile?.user?.phoneNumber && (
          <Tooltip title="Call">
            <IconButton size="small" onClick={handleContactPhone}>
              <Phone />
            </IconButton>
          </Tooltip>
        )}
        
        {maidProfile?.user?.email && (
          <Tooltip title="Email">
            <IconButton size="small" onClick={handleContactEmail}>
              <Email />
            </IconButton>
          </Tooltip>
        )}

        {onRejectMaid && (
          <Tooltip title="Reject Technician">
            <IconButton size="small" onClick={handleRejectClick} color="error">
              <Block />
            </IconButton>
          </Tooltip>
        )}
        
        {showStatusToggle && (
          <MaidStatusToggle
            maidProfile={maidProfile}
            onStatusChange={onStatusChange}
            variant="chip"
            showLabel={false}
          />
        )}
        
        {/* Reject Confirmation Dialog - Include in inline variant too */}
        <Dialog
          open={rejectDialogOpen}
          onClose={handleRejectCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            Reject Technician Application
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action will permanently reject the technician's application. They will be notified and will not be able to reapply.
            </Alert>
            
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Technician:</strong> {maidProfile?.firstName} {maidProfile?.lastName}
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Email:</strong> {maidProfile?.user?.email || maidProfile?.email}
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason for rejection *"
              placeholder="Please provide a reason for rejecting this technician's application..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              error={!rejectReason.trim() && rejectReason !== ''}
              helperText={!rejectReason.trim() && rejectReason !== '' ? 'Please provide a reason for rejection' : ''}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRejectCancel} disabled={rejecting}>
              Cancel
            </Button>
            <Button
              onClick={handleRejectConfirm}
              color="error"
              variant="contained"
              disabled={rejecting || !rejectReason.trim()}
              sx={{ minWidth: 100 }}
            >
              {rejecting ? 'Rejecting...' : 'Reject Technician'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Default menu variant
  return (
    <Box>
      <Tooltip title="More actions">
        <IconButton onClick={handleMenuOpen}>
          <MoreVert />
        </IconButton>
      </Tooltip>
      
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 200 }
        }}
      >
        <MenuItem onClick={handleViewDetails}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage Profile</ListItemText>
        </MenuItem>
        
        
        <Divider />
        
        {maidProfile?.user?.phoneNumber && (
          <MenuItem onClick={handleContactPhone}>
            <ListItemIcon>
              <Phone fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              Call {maidProfile.user.phoneNumber}
            </ListItemText>
          </MenuItem>
        )}
        
        {maidProfile?.user?.email && (
          <MenuItem onClick={handleContactEmail}>
            <ListItemIcon>
              <Email fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              Email {maidProfile.user.email}
            </ListItemText>
          </MenuItem>
        )}
        
        <Divider />
        
        <MenuItem onClick={handleViewBookings}>
          <ListItemIcon>
            <Assignment fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Bookings</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleViewPerformance}>
          <ListItemIcon>
            <Timeline fontSize="small" />
          </ListItemIcon>
          <ListItemText>Performance Metrics</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleViewServiceAreas}>
          <ListItemIcon>
            <LocationOn fontSize="small" />
          </ListItemIcon>
          <ListItemText>Service Areas</ListItemText>
        </MenuItem>

        {onRejectMaid && (
          <>
            <Divider />
            <MenuItem onClick={handleRejectClick} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <Block fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Reject Technician</ListItemText>
            </MenuItem>
          </>
        )}
        
        {showStatusToggle && (
          <>
            <Divider />
            <Box px={2} py={1}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Status Control
              </Typography>
              <MaidStatusToggle
                maidProfile={maidProfile}
                onStatusChange={onStatusChange}
                variant="button"
                showLabel={false}
                confirmAction={true}
              />
            </Box>
          </>
        )}
      </Menu>

      {/* Reject Confirmation Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={handleRejectCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" />
          Reject Technician Application
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will permanently reject the technician's application. They will be notified and will not be able to reapply.
          </Alert>
          
          <Typography variant="body1" sx={{ mb: 2 }}>
            <strong>Technician:</strong> {maidProfile?.firstName} {maidProfile?.lastName}
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            <strong>Email:</strong> {maidProfile?.user?.email || maidProfile?.email}
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason for rejection *"
            placeholder="Please provide a reason for rejecting this technician's application..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            error={!rejectReason.trim() && rejectReason !== ''}
            helperText={!rejectReason.trim() && rejectReason !== '' ? 'Please provide a reason for rejection' : ''}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRejectCancel} disabled={rejecting}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={rejecting || !rejectReason.trim()}
            sx={{ minWidth: 100 }}
          >
            {rejecting ? 'Rejecting...' : 'Reject Technician'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MaidQuickActions;